import { useMemo, useState } from 'react';

import { Link } from 'react-router';
import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';

import AuthShell, { AuthError, AuthNotice } from '@/components/auth/auth-shell';
import Button from '@/components/ui/button';
import Field, { inputCls } from '@/components/ui/field';
import * as auth from '@/auth/admin-auth.api';
import { requiredEmail, useValidation } from '@/lib/validation';

const forgotSchema = z.object({ email: requiredEmail });

/**
 * Asks for a reset link.
 *
 * The API answers identically whether or not the account exists, so this shows
 * the server's own message rather than implying anything about the address.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const draft = useMemo(() => ({ email }), [email]);
  const validation = useValidation(forgotSchema, draft);

  const submit = async () => {
    if (!validation.check()) return;

    setBusy(true);
    setError(null);
    try {
      const result = await auth.forgotPassword(email.trim());
      setSent(result.message);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not reach the server. Try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Reset your password"
      hint="We will email a link to set a new one."
      footer={
        <Link
          to="/login"
          className="text-ink-muted underline underline-offset-4 hover:text-brand-text"
        >
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <AuthNotice>{sent}</AuthNotice>
      ) : (
        <form
          className="flex flex-col gap-5"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <Field label="Email" required {...validation.field('email')}>
            <input
              type="email"
              className={inputCls}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="username"
              autoFocus
            />
          </Field>

          {error && <AuthError>{error}</AuthError>}

          <Button type="submit" className="w-full" disabled={busy}>
            {busy ? 'Sending…' : 'Send the link'}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}
